import React, { createContext, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';

interface LoadingContextValue {
  isLoading: boolean;
  setLoading: (id: string, loading: boolean) => void;
}

const LoadingContext = createContext<LoadingContextValue | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const loadingSourcesRef = useRef<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const loadingStartTimeRef = useRef<number | null>(null);
  const minimumLoadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setLoading = useCallback((id: string, loading: boolean) => {
    if (loading) {
      // Add this source to the loading set
      loadingSourcesRef.current.add(id);
      
      // Record start time if this is the first loading source
      if (loadingSourcesRef.current.size === 1) {
        loadingStartTimeRef.current = Date.now();
      }
      
      setIsLoading(true);
    } else {
      // Remove this source from the loading set
      loadingSourcesRef.current.delete(id);
      
      // Only stop loading if no sources are left
      if (loadingSourcesRef.current.size === 0) {
        const elapsedTime = Date.now() - (loadingStartTimeRef.current || 0);
        const remainingTime = Math.max(0, 1500 - elapsedTime); // 1.5s minimum
        
        // Clear any existing timeout
        if (minimumLoadingTimeoutRef.current) {
          clearTimeout(minimumLoadingTimeoutRef.current);
        }
        
        // Wait for the remaining time before hiding
        minimumLoadingTimeoutRef.current = setTimeout(() => {
          setIsLoading(false);
          loadingStartTimeRef.current = null;
        }, remainingTime);
      }
    }
  }, []);

  return (
    <LoadingContext.Provider value={{ isLoading, setLoading }}>
      {children}
    </LoadingContext.Provider>
  );
};

export default LoadingContext;
